import React, { useState } from 'react';
import { MapPin, Navigation, ChevronRight, X, Loader2 } from 'lucide-react';
import { isVenueClaimed } from './venueHelpers';

const VenueMapSection = React.memo(({ venue, googleMapsApiKey }) => {
  const [showDirectionsModal, setShowDirectionsModal] = useState(false);

  if (!(venue.latitude && venue.longitude) && !venue.address) return null;
  // KVKK: Onaysız mekanlarda tam konum/harita gizlenir
  if (!isVenueClaimed(venue)) return null;

  return (
    <>
      <div className="bg-white rounded-3xl shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100 overflow-hidden">
        <div className="p-6 sm:p-8">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="bg-gradient-to-r from-red-500 to-pink-600 p-3 rounded-2xl shadow-lg">
                <MapPin className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Konum & Harita</h2>
                <p className="text-sm text-gray-500">Bize nasıl ulaşırsınız</p>
              </div>
            </div>
            <button
              onClick={() => setShowDirectionsModal(true)}
              className="flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-blue-500 to-indigo-600 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300"
            >
              <Navigation className="w-5 h-5" />
              <span className="hidden sm:inline">Yol Tarifi</span>
            </button>
          </div>
          <div className="w-full h-[450px] rounded-2xl overflow-hidden shadow-xl ring-2 ring-gray-100">
            {venue.latitude && venue.longitude ? (
              <iframe
                key={googleMapsApiKey || 'loading'}
                width="100%"
                height="100%"
                style={{ border: 0 }}
                loading="lazy"
                allowFullScreen
                referrerPolicy="no-referrer-when-downgrade"
                src={googleMapsApiKey ? `https://www.google.com/maps/embed/v1/place?key=${googleMapsApiKey}&q=${venue.latitude},${venue.longitude}&zoom=15` : ''}
                title={`${venue.name} konumu`}
              />
            ) : (
              <iframe
                key={googleMapsApiKey || 'loading'}
                width="100%"
                height="100%"
                style={{ border: 0 }}
                loading="lazy"
                allowFullScreen
                referrerPolicy="no-referrer-when-downgrade"
                src={googleMapsApiKey ? `https://www.google.com/maps/embed/v1/place?key=${googleMapsApiKey}&q=${encodeURIComponent(venue.address + ', ' + venue.districtName + ', ' + venue.cityName)}` : ''}
                title={`${venue.name} konumu`}
              />
            )}
          </div>
          {!googleMapsApiKey && (
            <div className="mt-4 flex items-center justify-center gap-2 text-gray-500">
              <Loader2 className="w-5 h-5 animate-spin text-pink-600" />
              <p className="text-sm font-medium">Harita yükleniyor...</p>
            </div>
          )}
        </div>
      </div>

      {/* Directions Modal */}
      {showDirectionsModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowDirectionsModal(false)}>
          <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full p-6 transform animate-in fade-in zoom-in duration-300" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="bg-gradient-to-r from-blue-500 to-indigo-600 p-3 rounded-2xl">
                  <Navigation className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900">Yol Tarifi Al</h3>
                  <p className="text-sm text-gray-500">Navigasyon uygulaması seçin</p>
                </div>
              </div>
              <button onClick={() => setShowDirectionsModal(false)} className="p-2 hover:bg-gray-100 rounded-full transition-colors">
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            <div className="space-y-3">
              <a
                href={venue.latitude && venue.longitude 
                  ? `https://www.google.com/maps/dir/?api=1&destination=${venue.latitude},${venue.longitude}`
                  : `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(venue.address + ', ' + venue.districtName + ', ' + venue.cityName)}`
                }
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-4 p-4 bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-200 rounded-2xl hover:border-green-400 hover:shadow-lg transition-all duration-300 group"
                onClick={() => setShowDirectionsModal(false)}
              >
                <div className="w-14 h-14 bg-white rounded-2xl shadow-md flex items-center justify-center group-hover:scale-110 transition-transform">
                  <svg viewBox="0 0 24 24" className="w-8 h-8">
                    <path fill="#4285F4" d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"/>
                    <circle fill="#EA4335" cx="12" cy="9" r="2.5"/>
                  </svg>
                </div>
                <div className="flex-1">
                  <h4 className="font-bold text-gray-900 text-lg">Google Maps ile Yol Tarifi Al</h4>
                  <p className="text-sm text-gray-500">Google Haritalar yeni sekmede açılacak</p>
                </div>
                <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-green-600 group-hover:translate-x-1 transition-all" />
              </a>
            </div>
          </div>
        </div>
      )}
    </>
  );
});

VenueMapSection.displayName = 'VenueMapSection';
export default VenueMapSection;
